import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { toast } from "sonner";
import { usePersistentSort } from "@/lib/usePersistentSort";
import { Plus, Trash2, Pencil, ArrowUp, ArrowDown } from "lucide-react";

const CURRENCIES = ["USD", "CAD", "GBP", "EUR", "INR", "AUD", "AED", "SAR"];
const STATUSES = ["approved", "hold", "change_order", "cancelled", "completed"];
const DEFAULT_STAGES = [
  { stage_number: 1, stage_name: "On Approval", percentage: 50, expected_invoice_date: "" },
  { stage_number: 2, stage_name: "Final", percentage: 50, expected_invoice_date: "" },
];

export default function Projects() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [bds, setBds] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bdFilter, setBdFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const emptyForm = {
    client_id: "", project_name: "", project_description: "",
    project_type: "", project_location: "",
    project_amount: 0, currency: "USD",
    project_start_date: "", expected_submission_date: "", client_approval_date: "",
    status: "approved", notes: "",
    business_development_owner_id: "",
    payment_stages: DEFAULT_STAGES.map(s => ({ ...s })),
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const { data } = await api.get(`/api/projects?q=${encodeURIComponent(q)}${statusFilter !== "all" ? `&status=${statusFilter}` : ""}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [q, statusFilter]);
  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data));
    api.get("/api/service-types").then((r) => setServiceTypes(r.data || [])).catch(() => {});
    api.get("/api/users").then((r) => {
      const users = r.data || [];
      setAllUsers(users);
      setBds(users.filter((u) => u.role === "business_development"));
    }).catch(() => {});
  }, []);

  const bdMap = React.useMemo(() => {
    const m = new Map(); allUsers.forEach((u) => m.set(u.id, u)); return m;
  }, [allUsers]);
  const activeBds = bds.filter((u) => u.is_active !== false);
  const [sort, setSort] = usePersistentSort("projects", { key: "created_at", dir: "desc" });
  const filteredItems = React.useMemo(
    () => items.filter((p) => bdFilter === "all" || p.business_development_owner_id === bdFilter),
    [items, bdFilter]
  );
  const sortedItems = React.useMemo(() => {
    const arr = [...filteredItems];
    arr.sort((a, b) => {
      const k = sort.key;
      const num = k === "current_project_value";
      const dateLike = k === "created_at" || k.endsWith("_date") || k.endsWith("_at");
      let va = a?.[k]; let vb = b?.[k];
      if (num) { va = Number(va || 0); vb = Number(vb || 0); }
      else if (dateLike) { va = va || ""; vb = vb || ""; }
      else { va = (va ?? "").toString().toLowerCase(); vb = (vb ?? "").toString().toLowerCase(); }
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredItems, sort]);

  const totalPct = form.payment_stages.reduce((a, s) => a + Number(s.percentage || 0), 0);

  const create = async () => {
    if (!form.client_id) return toast.error("Client is required");
    if (!form.project_name) return toast.error("Project name required");
    if (Number(form.project_amount) <= 0) return toast.error("Amount must be > 0");
    if (Math.abs(totalPct - 100) > 0.01) return toast.error("Payment stages must total 100%");
    try {
      await api.post("/api/projects", {
        ...form,
        project_amount: Number(form.project_amount),
        business_development_owner_id: form.business_development_owner_id || undefined,
      });
      toast.success("Project created");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openEdit = async (p) => {
    const { data } = await api.get(`/api/projects/${p.id}`);
    setEditing(p);
    setForm({
      ...emptyForm,
      ...data.project,
      project_start_date: data.project.project_start_date?.slice(0, 10) || "",
      expected_submission_date: data.project.expected_submission_date?.slice(0, 10) || "",
      actual_submission_date: data.project.actual_submission_date?.slice(0, 10) || "",
      client_approval_date: data.project.client_approval_date?.slice(0, 10) || "",
      payment_stages: (data.payment_stages || []).map((s) => ({
        id: s.id,
        stage_number: s.stage_number, stage_name: s.stage_name,
        percentage: s.percentage, trigger: s.trigger || "",
        expected_invoice_date: s.expected_invoice_date?.slice(0, 10) || "",
      })),
    });
  };

  const saveEdit = async () => {
    if (Math.abs(totalPct - 100) > 0.01) return toast.error("Payment stages must total 100%");
    try {
      const body = {
        project_name: form.project_name,
        project_description: form.project_description,
        project_type: form.project_type,
        project_location: form.project_location,
        project_amount: Number(form.project_amount),
        currency: form.currency,
        project_start_date: form.project_start_date || null,
        expected_submission_date: form.expected_submission_date || null,
        actual_submission_date: form.actual_submission_date || null,
        client_approval_date: form.client_approval_date || null,
        status: form.status,
        notes: form.notes,
        payment_stages: form.payment_stages,
      };
      // Project code changes use a dedicated audit-logged endpoint below.
      if (user?.role === "admin" && form.business_development_owner_id) {
        body.business_development_owner_id = form.business_development_owner_id;
      }
      await api.patch(`/api/projects/${editing.id}`, body);
      toast.success("Project updated");
      setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.project_name}"? Linked invoices and payments remain in history. Only admins can delete.`)) return;
    try { await api.delete(`/api/projects/${p.id}`); toast.success("Project deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updStage = (i, k, v) => {
    const s = [...form.payment_stages];
    s[i] = { ...s[i], [k]: k === "percentage" ? Number(v) : v };
    setForm({ ...form, payment_stages: s });
  };
  const removeStage = (i) => setForm({ ...form, payment_stages: form.payment_stages.filter((_, idx) => idx !== i) });
  const addStage = () => {
    setForm({ ...form, payment_stages: [...form.payment_stages, { _key: (crypto.randomUUID?.() || `s-${Date.now()}-${Math.random()}`), stage_number: form.payment_stages.length + 1, stage_name: "New Stage", percentage: 0, expected_invoice_date: "" }] });
  };
  const moveStage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.payment_stages.length) return;
    const arr = [...form.payment_stages];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    // renumber
    arr.forEach((s, idx) => { s.stage_number = idx + 1; });
    setForm({ ...form, payment_stages: arr });
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Projects</h1>
          <p className="text-sm text-slate-500 mt-1">All studio projects and their payment stages.</p>
        </div>
        {hasRole(user, "admin", "business_development") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-project-btn" className="bg-brand hover:bg-brand-hover">
                <Plus className="w-4 h-4 mr-2" /> Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label>Client *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="project-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.firm_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Project Name *</Label>
                  <Input data-testid="project-name" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Amount *</Label>
                  <Input data-testid="project-amount" type="number" value={form.project_amount} onChange={(e) => setForm({ ...form, project_amount: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Service / Project Type</Label>
                  <Select value={form.project_type || "__unset__"} onValueChange={(v) => setForm({ ...form, project_type: v === "__unset__" ? "" : v })}>
                    <SelectTrigger data-testid="project-type-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unset__">— None —</SelectItem>
                      {serviceTypes.filter((s) => s.is_active).map((s) => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Location</Label>
                  <Input value={form.project_location} onChange={(e) => setForm({ ...form, project_location: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Start Date</Label>
                  <Input type="date" value={form.project_start_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, project_start_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Client Approval Date <span className="text-slate-400 text-xs">(needed to auto-generate the project code)</span></Label>
                  <Input type="date" value={form.client_approval_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, client_approval_date: e.target.value })} data-testid="project-approval-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Expected Submission</Label>
                  <Input type="date" value={form.expected_submission_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, expected_submission_date: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {user?.role === "admin" && (
                  <div className="col-span-2 space-y-1.5">
                    <Label>Assigned BD <span className="text-slate-400 text-xs">(inherits from client if empty)</span></Label>
                    <Select value={form.business_development_owner_id} onValueChange={(v) => setForm({ ...form, business_development_owner_id: v })}>
                      <SelectTrigger data-testid="project-bd"><SelectValue placeholder="Inherit from client" /></SelectTrigger>
                      <SelectContent>
                        {activeBds.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2 space-y-1.5">
                  <Label>Description</Label>
                  <Textarea value={form.project_description} onChange={(e) => setForm({ ...form, project_description: e.target.value })} />
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Payment Stages ({totalPct.toFixed(1)}%)</Label>
                    <Button size="sm" variant="outline" onClick={addStage}><Plus className="w-3 h-3 mr-1" /> Stage</Button>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    Set the exact date each invoice should be raised — Accounts uses this as their to-do list per client's custom payment terms.
                  </div>
                  <div className="space-y-2">
                    {form.payment_stages.map((s, i) => (
                      <div key={s.id || s._key || `stage-${i}`} className="border border-slate-200 rounded-md p-2">
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-1 text-xs font-medium text-slate-500">{s.stage_number}</div>
                          <Input className="col-span-6" value={s.stage_name} onChange={(e) => updStage(i, "stage_name", e.target.value)} placeholder="Stage name" />
                          <Input className="col-span-3" type="number" value={s.percentage} onChange={(e) => updStage(i, "percentage", e.target.value)} placeholder="%" />
                          <div className="col-span-1 text-xs text-slate-500 text-right">
                            {formatCurrency(Number(form.project_amount || 0) * s.percentage / 100, form.currency)}
                          </div>
                          <button className="col-span-1 justify-self-end text-slate-400 hover:text-rose-600" onClick={() => removeStage(i)}><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-12 gap-2 items-center mt-2">
                          <div className="col-span-1 text-[10px] uppercase text-slate-400 text-right">Invoice on</div>
                          <Input
                            className="col-span-4"
                            type="date"
                            value={s.expected_invoice_date?.slice(0, 10) || ""}
                            onChange={(e) => updStage(i, "expected_invoice_date", e.target.value)}
                            data-testid={`stage-${i}-invoice-date`}
                          />
                          <Input
                            className="col-span-7"
                            value={s.trigger || ""}
                            onChange={(e) => updStage(i, "trigger", e.target.value)}
                            placeholder="Milestone / trigger (e.g. On design approval)"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {Math.abs(totalPct - 100) > 0.01 && (
                    <div className="text-xs text-rose-600 mt-2">⚠ Percentages must total 100%. Currently {totalPct.toFixed(1)}%.</div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-brand hover:bg-brand-hover" onClick={create} data-testid="save-project-btn">Create Project</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="sticky top-0 z-20 bg-white -m-4 mb-4 px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <Input placeholder="Search by name or code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="projects-search" className="flex-1 min-w-[200px]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="project-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={bdFilter} onValueChange={setBdFilter}>
            <SelectTrigger className="w-44" data-testid="project-bd-filter"><SelectValue placeholder="All BDs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All BDs</SelectItem>
              {bds.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? " (inactive)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={sort.key === "created_at" ? (sort.dir === "desc" ? "newest" : "oldest") : "custom"}
            onValueChange={(v) => {
              if (v === "newest") setSort({ key: "created_at", dir: "desc" });
              else if (v === "oldest") setSort({ key: "created_at", dir: "asc" });
            }}
          >
            <SelectTrigger className="w-44" data-testid="project-sort-order"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              {sort.key !== "created_at" && <SelectItem value="custom" disabled>Custom · {sort.key}</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              {[
                ["project_code", "Code"],
                ["project_name", "Project"],
                ["firm_name_snapshot", "Client"],
                ["business_development_owner_name_snapshot", "BD"],
                ["current_project_value", "Amount", true],
                ["status", "Status"],
                ["payment_status", "Payment"],
                ["created_at", "Created", false, true],
              ].map(([k, l, num, dateLike]) => (
                <th key={k}>
                  <button type="button" data-testid={`sort-${k}`}
                    onClick={() => setSort((p) => p.key === k
                      ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" }
                      : { key: k, dir: (num || dateLike) ? "desc" : "asc" })}
                    className="inline-flex items-center gap-1 hover:text-slate-900">
                    {l}
                    <span className="text-[10px] text-slate-400">{sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                  </button>
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`}>
                <td className="font-mono text-xs cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)}>{p.project_code || <span className="text-slate-400 italic">—</span>}</td>
                <td className="font-medium cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)}>{p.project_name}</td>
                <td className="text-slate-600">{p.firm_name_snapshot}</td>
                <td className="text-slate-600" data-testid={`project-bd-cell-${p.id}`}>
                  {(() => {
                    const owner = p.business_development_owner_id ? bdMap.get(p.business_development_owner_id) : null;
                    const name = owner?.name || owner?.display_name || p.business_development_owner_name_snapshot;
                    if (name) return name;
                    return <span className="text-slate-400 italic">unassigned</span>;
                  })()}
                </td>
                <td>
                  <div className="tabular-nums">{formatCurrency(p.current_project_value ?? p.project_amount, p.currency)}</div>
                  {p.current_project_value != null && Math.abs((p.current_project_value || 0) - (p.original_project_amount ?? p.project_amount ?? 0)) > 0.01 && (
                    <div className="text-[10px] text-slate-500 leading-tight" title={`Original ${formatCurrency(p.original_project_amount ?? p.project_amount, p.currency)} + Additions ${formatCurrency(p.approved_additional_work || 0, p.currency)} − Deductions ${formatCurrency(p.approved_deductions || 0, p.currency)}`}>
                      Orig {formatCurrency(p.original_project_amount ?? p.project_amount, p.currency)}
                      {(p.approved_additional_work || 0) > 0 && <> · <span className="text-emerald-700">+{formatCurrency(p.approved_additional_work, p.currency)}</span></>}
                      {(p.approved_deductions || 0) > 0 && <> · <span className="text-rose-700">-{formatCurrency(p.approved_deductions, p.currency)}</span></>}
                    </div>
                  )}
                </td>
                <td><StatusBadge status={p.status} /></td>
                <td><StatusBadge status={p.payment_status} /></td>
                <td className="text-slate-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                <td>
                  <div className="flex gap-1">
                    {hasRole(user, "admin", "business_development") && (
                      <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    )}
                    {hasRole(user, "admin") && (
                      <button className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => remove(p)} data-testid={`delete-project-${p.id}`} title="Delete (admin only)"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredItems.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-500 py-8">No projects match the current filters.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Project · {editing?.project_code || editing?.project_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {user?.role === "admin" && (
              <div className="col-span-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2 text-xs text-slate-600">
                <span>Current Project Code:</span>
                <code className="font-mono text-slate-800">{editing?.project_code || "—"}</code>
                <span className="ml-auto text-slate-400">Use "Administrative Controls" below to rename (audit-logged)</span>
              </div>
            )}
            <div className="col-span-2 space-y-1.5"><Label>Project Name</Label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Amount</Label><Input type="number" value={form.project_amount} onChange={(e) => setForm({ ...form, project_amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={form.project_type || "__unset__"} onValueChange={(v) => setForm({ ...form, project_type: v === "__unset__" ? "" : v })}>
                <SelectTrigger data-testid="edit-project-type-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset__">— None —</SelectItem>
                  {/* Keep the current value visible even if it's an archived / legacy type */}
                  {form.project_type && !serviceTypes.some((s) => s.name === form.project_type) && (
                    <SelectItem value={form.project_type}>{form.project_type} <span className="text-slate-400 text-xs">(legacy)</span></SelectItem>
                  )}
                  {serviceTypes.filter((s) => s.is_active || s.name === form.project_type).map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}{s.is_active === false ? " (archived)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Location</Label><Input value={form.project_location || ""} onChange={(e) => setForm({ ...form, project_location: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Start Date</Label><Input type="date" value={form.project_start_date || ""} onChange={(e) => setForm({ ...form, project_start_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Expected Submission</Label><Input type="date" value={form.expected_submission_date || ""} onChange={(e) => setForm({ ...form, expected_submission_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Actual Submission</Label><Input type="date" value={form.actual_submission_date || ""} onChange={(e) => setForm({ ...form, actual_submission_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Client Approval</Label><Input type="date" value={form.client_approval_date || ""} onChange={(e) => setForm({ ...form, client_approval_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {user?.role === "admin" && (
              <div className="col-span-2 space-y-1.5">
                <Label>Assigned BD <span className="text-slate-400 text-xs">(admin-only override)</span></Label>
                <Select value={form.business_development_owner_id || ""} onValueChange={(v) => setForm({ ...form, business_development_owner_id: v })}>
                  <SelectTrigger data-testid="edit-project-bd"><SelectValue placeholder="Inherit from client" /></SelectTrigger>
                  <SelectContent>
                    {bds.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? " (inactive)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2 space-y-1.5"><Label>Description</Label><Textarea value={form.project_description || ""} onChange={(e) => setForm({ ...form, project_description: e.target.value })} /></div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Payment Stages ({totalPct.toFixed(1)}%)</Label>
                <Button size="sm" variant="outline" onClick={addStage}><Plus className="w-3 h-3 mr-1" /> Stage</Button>
              </div>
              <div className="space-y-2">
                {form.payment_stages.map((s, i) => (
                  <div key={s.id || s._key || `stage-${i}`} className="border border-slate-200 rounded-md p-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1 flex items-center gap-0.5">
                        <span className="text-xs font-medium text-slate-500 w-4">{s.stage_number}</span>
                        <button type="button" className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === 0} onClick={() => moveStage(i, -1)} title="Move up"><ArrowUp className="w-3 h-3" /></button>
                        <button type="button" className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === form.payment_stages.length - 1} onClick={() => moveStage(i, 1)} title="Move down"><ArrowDown className="w-3 h-3" /></button>
                      </div>
                      <Input className="col-span-5" value={s.stage_name} onChange={(e) => updStage(i, "stage_name", e.target.value)} />
                      <Input className="col-span-3" type="number" value={s.percentage} onChange={(e) => updStage(i, "percentage", e.target.value)} placeholder="%" />
                      <div className="col-span-2 text-xs text-slate-500 text-right">{formatCurrency(Number(form.project_amount || 0) * s.percentage / 100, form.currency)}</div>
                      <button className="col-span-1 justify-self-end text-slate-400 hover:text-rose-600" onClick={() => removeStage(i)} title={s.id ? "Delete (blocked if invoice linked)" : "Delete"}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center mt-2">
                      <div className="col-span-1 text-[10px] uppercase text-slate-400 text-right">Invoice on</div>
                      <Input className="col-span-4" type="date" value={s.expected_invoice_date?.slice(0, 10) || ""} onChange={(e) => updStage(i, "expected_invoice_date", e.target.value)} />
                      <Input className="col-span-7" value={s.trigger || ""} onChange={(e) => updStage(i, "trigger", e.target.value)} placeholder="Milestone / trigger" />
                    </div>
                  </div>
                ))}
              </div>
              {Math.abs(totalPct - 100) > 0.01 && <div className="text-xs text-rose-600 mt-2">⚠ Percentages must total 100%. Currently {totalPct.toFixed(1)}%.</div>}
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          {/* Administrative Controls (admin only) — audit-logged project code edit */}
          {user?.role === "admin" && editing && (
            <AdminControls project={editing} onCodeChanged={() => { load(); }} />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyForm); }}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveEdit} data-testid="save-edit-project">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AdminControls({ project, onCodeChanged }) {
  const [newCode, setNewCode] = useState(project?.project_code || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // BD-incentive sequence override (admin-only)
  const [seqValue, setSeqValue] = useState(
    project?.incentive_sequence_override == null ? "" : String(project.incentive_sequence_override)
  );
  const [seqReason, setSeqReason] = useState("");
  const [seqSaving, setSeqSaving] = useState(false);
  React.useEffect(() => {
    setNewCode(project?.project_code || "");
    setReason("");
    setSeqValue(project?.incentive_sequence_override == null ? "" : String(project.incentive_sequence_override));
    setSeqReason("");
  }, [project?.id, project?.incentive_sequence_override]);

  const save = async () => {
    if (!newCode.trim()) return toast.error("Enter a new project code");
    if (!reason.trim()) return toast.error("Reason is required");
    if (newCode.trim() === (project?.project_code || "")) return toast.error("New code is the same as the current one");
    setSaving(true);
    try {
      await api.post(`/api/projects/${project.id}/edit-code`, { new_code: newCode.trim(), reason: reason.trim() });
      toast.success("Project code updated (audit-logged)");
      onCodeChanged && onCodeChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveSeq = async () => {
    const trimmed = seqValue.trim();
    let payloadSeq = null;
    if (trimmed !== "") {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 1 || Math.floor(n) !== n) {
        return toast.error("Sequence must be a positive whole number (or blank to clear)");
      }
      payloadSeq = n;
    }
    setSeqSaving(true);
    try {
      await api.patch(`/api/projects/${project.id}/incentive-sequence`, {
        sequence: payloadSeq, reason: seqReason.trim() || null,
      });
      toast.success(payloadSeq == null
        ? "Override cleared — natural order restored"
        : `BD incentive sequence pinned to ${payloadSeq} — all historical payments recomputed`);
      onCodeChanged && onCodeChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSeqSaving(false); }
  };

  return (
    <div className="border-t border-slate-200 pt-4 mt-2 space-y-3">
      <div className="text-xs uppercase tracking-wider text-rose-600 font-medium">Administrative Controls</div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">New Project Code</Label>
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} data-testid="admin-new-code" className="font-mono" />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label className="text-xs">Reason *</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-code-reason" placeholder="Explain why this code is being changed" />
        </div>
      </div>
      <div className="flex justify-between items-center">
        <div className="text-xs text-slate-500">
          {project?.project_code_history?.length ? `${project.project_code_history.length} previous change(s) on record` : "No prior code changes"}
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-rose-700 hover:bg-rose-800" data-testid="save-code-edit">
          {saving ? "Saving…" : "Change Project Code"}
        </Button>
      </div>

      {/* BD incentive sequence override — admin-only pin for the ladder position */}
      <div className="border-t border-slate-200 pt-3 mt-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-rose-600 font-medium">BD Incentive Sequence</div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Pins where this project sits in the BD's project ladder for incentive math.
          Leave blank to derive the position from the natural created-at order
          (auto re-ranks when sibling projects are deleted).
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Sequence # <span className="text-slate-400">(1 = first)</span></Label>
            <Input
              type="number"
              min={1}
              step={1}
              value={seqValue}
              onChange={(e) => setSeqValue(e.target.value)}
              placeholder="auto"
              data-testid="admin-seq-value"
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Reason <span className="text-slate-400">(optional, audit-logged)</span></Label>
            <Input
              value={seqReason}
              onChange={(e) => setSeqReason(e.target.value)}
              placeholder="e.g. legacy import ordering, migration fix"
              data-testid="admin-seq-reason"
            />
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="text-xs text-slate-500">
            Current override: <b className="text-slate-700">{project?.incentive_sequence_override ?? "— (auto)"}</b>
          </div>
          <Button size="sm" onClick={saveSeq} disabled={seqSaving} className="bg-rose-700 hover:bg-rose-800" data-testid="save-seq-override">
            {seqSaving ? "Saving…" : (seqValue.trim() === "" ? "Clear override" : "Pin sequence")}
          </Button>
        </div>
      </div>
    </div>
  );
}
